"use client";
import { useReducedMotion } from "motion/react";
import { useCallback, useEffect, useState } from "react";
const useCarouselAutoplay = (emblaApi)=>{
    const [autoplayIsPlaying, setAutoplayIsPlaying] = useState(false);
    const prefersReducedMotion = useReducedMotion() ?? false;
    const [autoplayIsManuallyPaused, setAutoplayIsManuallyPaused] = useState(prefersReducedMotion);
    const onAutoplayButtonClick = useCallback((callback)=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return void callback();
        autoplay.reset();
        callback();
    }, [
        emblaApi
    ]);
    const toggleAutoplay = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        if (prefersReducedMotion) {
            const newPausedState = !autoplayIsManuallyPaused;
            setAutoplayIsManuallyPaused(newPausedState);
            if (newPausedState) autoplay.stop();
            else {
                autoplay.reset();
                autoplay.play();
            }
        } else {
            const wasPlaying = autoplay.isPlaying();
            if (wasPlaying) {
                autoplay.stop();
                setAutoplayIsManuallyPaused(true);
            } else {
                setAutoplayIsManuallyPaused(false);
                autoplay.reset();
                autoplay.play();
            }
        }
    }, [
        emblaApi,
        prefersReducedMotion,
        autoplayIsManuallyPaused
    ]);
    const handleToggleClick = useCallback((event)=>{
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        toggleAutoplay();
    }, [
        toggleAutoplay
    ]);
    const manuallyPauseAutoplay = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (autoplay && autoplay.isPlaying()) {
            autoplay.stop();
            setAutoplayIsManuallyPaused(true);
        }
    }, [
        emblaApi
    ]);
    useEffect(()=>{
        if (!emblaApi || !prefersReducedMotion) return;
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        if (autoplay.isPlaying() && autoplayIsManuallyPaused) autoplay.stop();
    }, [
        emblaApi,
        prefersReducedMotion,
        autoplayIsManuallyPaused
    ]);
    useEffect(()=>{
        if (!emblaApi || !prefersReducedMotion) return;
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        if (autoplay.isPlaying() && autoplayIsManuallyPaused) autoplay.stop();
    }, [
        emblaApi,
        prefersReducedMotion,
        autoplayIsManuallyPaused
    ]);
    useEffect(()=>{
        if (!emblaApi) return;
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        const updateState = ()=>{
            const isPlaying = autoplay.isPlaying();
            setAutoplayIsPlaying(isPlaying);
        };
        updateState();
        const onPlay = ()=>{
            setAutoplayIsPlaying(true);
        };
        const onStop = ()=>{
            setAutoplayIsPlaying(false);
        };
        emblaApi.on('autoplay:play', onPlay).on('autoplay:stop', onStop).on('reInit', updateState);
        return ()=>{
            emblaApi.off('autoplay:play', onPlay).off('autoplay:stop', onStop);
        };
    }, [
        emblaApi
    ]);
    const effectiveAutoplayIsPlaying = prefersReducedMotion ? !autoplayIsManuallyPaused : autoplayIsPlaying;
    return {
        autoplayIsPlaying: effectiveAutoplayIsPlaying,
        autoplayIsManuallyPaused,
        toggleAutoplay: handleToggleClick,
        onAutoplayButtonClick,
        manuallyPauseAutoplay
    };
};
export { useCarouselAutoplay };
